import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import api from "../api/axiosInstance";

/**
 * Distinct category names from category/list rows (one row per
 * subcategory), in the order the storefront category bar shows them:
 * category_order ascending, then unordered categories by name
 * (case-insensitive) - the same rule the API uses.
 * @param {Array<{category: string, category_order?: number|null}>} rows
 * @returns {string[]}
 */
export const buildCategoryOrder = (rows = []) => {
    const orderByName = new Map();
    rows.forEach((row) => {
        const name = (row.category || "").trim();
        if (!name) return;
        const order = typeof row.category_order === "number" ? row.category_order : null;
        const current = orderByName.get(name);
        if (current === undefined || (order !== null && (current === null || order < current))) {
            orderByName.set(name, order);
        }
    });
    return [...orderByName.entries()]
        .sort(([aName, aOrder], [bName, bOrder]) => {
            if (aOrder !== null && bOrder !== null && aOrder !== bOrder) return aOrder - bOrder;
            if ((aOrder === null) !== (bOrder === null)) return aOrder === null ? 1 : -1;
            return aName.localeCompare(bName, "en", { sensitivity: "base" });
        })
        .map(([name]) => name);
};

/**
 * Applies a refreshed server order to a list the admin may have reordered
 * but not saved: keeps the admin's positions for names that still exist,
 * drops deleted ones and appends new ones in server order.
 * @param {string[]} current - order on screen (possibly unsaved)
 * @param {string[]} incoming - order from the refreshed rows
 * @returns {string[]}
 */
export const mergeOrder = (current, incoming) => {
    const incomingSet = new Set(incoming);
    const kept = current.filter((name) => incomingSet.has(name));
    const keptSet = new Set(kept);
    return [...kept, ...incoming.filter((name) => !keptSet.has(name))];
};

export const moveItem = (list, index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= list.length) return list;
    const next = [...list];
    [next[index], next[target]] = [next[target], next[index]];
    return next;
};

/**
 * Card for setting the order of the store's categories in its storefront
 * category bar. Store categories appear only on the store's own site.
 * @param {{rows: Array, businessId: string|null, onSaved?: Function}} props
 */
function CategoryBarOrder({ rows, businessId, onSaved }) {
    const { t } = useTranslation();
    const [order, setOrder] = useState([]);
    const [savedOrder, setSavedOrder] = useState([]);
    const [saving, setSaving] = useState(false);

    // Latest saved order, readable inside the rows effect without making it a
    // dependency (it must only re-run when rows change).
    const savedOrderRef = useRef([]);

    useEffect(() => {
        const incoming = buildCategoryOrder(rows);
        const previousSaved = savedOrderRef.current;
        savedOrderRef.current = incoming;
        setSavedOrder(incoming);
        // A refresh (e.g. a delete in the table below) must not throw away
        // moves the admin hasn't saved yet.
        setOrder((current) => {
            const unsaved = current.join("\u0000") !== previousSaved.join("\u0000");
            return unsaved ? mergeOrder(current, incoming) : incoming;
        });
    }, [rows]);

    const isDirty = order.join("\u0000") !== savedOrder.join("\u0000");

    const saveOrder = () => {
        if (!businessId) {
            toast.error(t("categoryTab.missingBusiness"));
            return;
        }
        setSaving(true);
        const payload = { categories: order, business_id: businessId };
        api
            .post("category/reorder", payload)
            .then((res) => {
                if (res.data.status === "success") {
                    savedOrderRef.current = order;
                    setSavedOrder(order);
                    toast.success(t("categoryTab.barOrderSaved"));
                    if (onSaved) onSaved(order);
                } else {
                    toast.error(res.data.message || t("categoryTab.barOrderError"));
                }
            })
            .catch((err) => {
                console.error("Error saving category order:", err);
                toast.error(t("categoryTab.barOrderError"));
            })
            .finally(() => setSaving(false));
    };

    if (order.length === 0) return null;

    return (
        <div className="card mb-3">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap">
                <div>
                    <h3 className="card-title mb-0">{t("categoryTab.barOrderTitle")}</h3>
                    <small className="text-muted d-block">{t("categoryTab.barOrderHint")}</small>
                </div>
                <div className="mt-2 mt-sm-0">
                    <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm mr-2"
                        disabled={!isDirty || saving}
                        onClick={() => setOrder(savedOrder)}
                    >
                        {t("categoryTab.barOrderReset")}
                    </button>
                    <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        disabled={!isDirty || saving}
                        onClick={saveOrder}
                    >
                        {saving ? t("categoryTab.barOrderSaving") : t("categoryTab.barOrderSave")}
                    </button>
                </div>
            </div>
            <div className="card-body">
                <div className="mb-3" aria-label="Category bar preview">
                    {order.map((name) => (
                        <span key={name} className="badge badge-pill badge-light border mr-1 mb-1 p-2">
                            {name}
                        </span>
                    ))}
                </div>
                <ol className="list-group">
                    {order.map((name, index) => (
                        <li
                            key={name}
                            className="list-group-item d-flex align-items-center justify-content-between py-2"
                        >
                            <span>
                                <span className="text-muted mr-2">{index + 1}.</span>
                                {name}
                            </span>
                            <span>
                                <button
                                    type="button"
                                    className="btn btn-light btn-sm mr-1"
                                    aria-label={t("categoryTab.barOrderMoveUp", { name })}
                                    disabled={index === 0}
                                    onClick={() => setOrder((prev) => moveItem(prev, index, -1))}
                                >
                                    <i className="fa-solid fa-arrow-up" />
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-light btn-sm"
                                    aria-label={t("categoryTab.barOrderMoveDown", { name })}
                                    disabled={index === order.length - 1}
                                    onClick={() => setOrder((prev) => moveItem(prev, index, 1))}
                                >
                                    <i className="fa-solid fa-arrow-down" />
                                </button>
                            </span>
                        </li>
                    ))}
                </ol>
            </div>
        </div>
    );
}

export default CategoryBarOrder;
